import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import WorkGrid from "@/components/feature/WorkGrid";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { getFeatured, getLatest, getPopular } from "@/utils/work";
import { specials } from "@/mocks/specials";
import { works } from "@/mocks/movies";

export default function Home() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 550);
    return () => window.clearTimeout(timer);
  }, []);

  const featured = getFeatured();
  const popular = getPopular(10);
  const latest = getLatest(10);

  const movieCount = works.filter((w) => w.type === "movie").length;
  const seriesCount = works.filter((w) => w.type === "tvseries").length;

  return (
    <SiteLayout>
      <HeroCarousel works={featured} loading={loading} />

      {/* channel entry strip */}
      <section className="mx-auto -mt-10 w-full px-4 md:px-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Link
            to="/category/movie"
            className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
          >
            <div>
              <h4 className="font-heading text-base font-bold text-foreground-950">
                韩国伦理电影
              </h4>
              <p className="mt-1 text-xs text-foreground-500">共 {movieCount} 部作品</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-2xl text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
              <i className="ri-movie-2-line" />
            </span>
          </Link>
          <Link
            to="/category/tvseries"
            className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
          >
            <div>
              <h4 className="font-heading text-base font-bold text-foreground-950">
                韩国伦理电视剧
              </h4>
              <p className="mt-1 text-xs text-foreground-500">共 {seriesCount} 部作品</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-2xl text-accent-600 transition-colors group-hover:bg-accent-500 group-hover:text-background-50">
              <i className="ri-live-line" />
            </span>
          </Link>
          <Link
            to="/specials"
            className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
          >
            <div>
              <h4 className="font-heading text-base font-bold text-foreground-950">
                精选影视专题
              </h4>
              <p className="mt-1 text-xs text-foreground-500">共 {specials.length} 个专题</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-secondary-100 text-2xl text-secondary-600 transition-colors group-hover:bg-secondary-500 group-hover:text-background-50">
              <i className="ri-bookmark-3-line" />
            </span>
          </Link>
        </div>
      </section>

      {/* popular */}
      <section className="mx-auto w-full px-4 py-14 md:px-8">
        <SectionHeading
          eyebrow="热门推荐"
          title="当前最受欢迎的伦理影视"
          description="根据播放热度与观众关注度综合排序，看看大家都在看哪些伦理作品。"
          linkTo="/category/movie"
          linkLabel="查看全部"
        />
        <WorkGrid
          works={popular}
          loading={loading}
          skeletonCount={5}
          columns={4}
          emptyTitle="暂无热门内容"
          emptyDescription="热门榜单正在更新中，请稍后再来。"
        />
      </section>

      {/* new arrivals */}
      <section className="bg-background-50 py-14">
        <div className="mx-auto w-full px-4 md:px-8">
          <SectionHeading
            eyebrow="最新上线"
            title="新上线影视作品"
            description="按更新日期倒序排列，第一时间发现刚刚上线的新片与新剧。"
            linkTo="/category/tvseries"
            linkLabel="浏览剧集"
          />
          <WorkGrid
            works={latest}
            loading={loading}
            skeletonCount={5}
            columns={4}
            emptyTitle="暂无最新内容"
            emptyDescription="新片正在陆续上架，敬请期待。"
          />
        </div>
      </section>

      {/* specials */}
      <section className="mx-auto w-full px-4 py-14 md:px-8">
        <SectionHeading
          eyebrow="专题精选"
          title="伦理影视专题推荐"
          description="按主题为你整理的观影合集，从经典到热播，一次补齐想看的内容。"
          linkTo="/specials"
          linkLabel="全部专题"
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {specials.slice(0, 3).map((special) => (
            <Link
              key={special.id}
              to={`/category/${special.type}?filter=${encodeURIComponent(special.filter)}`}
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={special.cover}
                  alt={`${special.title} 专题封面`}
                  title={`${special.title} 韩国伦理影视专题`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-md bg-accent-500 px-2 py-1 text-[11px] font-bold text-background-50">
                  {special.accent}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  {special.title}
                </h3>
                <p className="mt-1 text-xs font-medium text-accent-600">{special.subtitle}</p>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">
                  {special.description}
                </p>
                <span className="mt-3 flex items-center gap-1 text-sm font-semibold text-primary-600">
                  进入专题
                  <i className="ri-arrow-right-s-line text-base" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* notice / cta */}
      <section className="mx-auto w-full px-4 pb-16 md:px-8">
        <div className="flex flex-col items-start justify-between gap-5 rounded-lg bg-primary-900 px-6 py-8 md:flex-row md:items-center md:px-10">
          <div>
            <h3 className="font-heading text-lg font-bold text-background-50 md:text-xl">
              找到喜欢的伦理影视作品了吗？
            </h3>
            <p className="mt-2 max-w-xl text-sm text-background-300">
              使用搜索功能，输入片名、导演或演员，快速定位想看的内容。本站仅提供作品信息展示，不提供在线播放服务。
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/search"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-search-line text-base" />
              立即搜索
            </Link>
            <Link
              to="/about"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-information-line text-base" />
              了解我们
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}